import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch, ensureAuthOrRedirect,  redirectToLogin, attachExpiryTimer, getToken } from "../utils/auth";

export default function ExportCSVPanel({
  open,
  onClose,
  onExport,            // (payload) => Promise<void> | void
  credits = 0,
  defaultDomain = "",
  defaultStart = "",
  defaultEnd = "",
  query
}) {
  const [domain, setDomain] = useState(query);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const [fields, setFields] = useState({
    hostname: false,
    ipAddress: false,
    osInfo: false,
    antivirus: false,
    malwarePath: false,
    countryCode: false,
  });

  const [types, setTypes] = useState({
    consumer: false,
    corporate: false,
  });

  const [isSubdomain, setIsSubdomain] = useState(null); // null = not answered, true = yes, false = no

  useEffect(() => {
  setDomain(query ?? "");
}, [query]);

  function getJwtToken() {
  try {
    return (
      localStorage.getItem("jwtToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      window.WHITEINTEL_TOKEN || // optional global injection
      ""
    );
  } catch {
    return "";
  }
}

  const [exportLimit, setExportLimit] = useState(null);

  useEffect(() => {
    if (!open) return; // only fetch when panel is opened

    const fetchLimit = async () => {
      try {
        const token = getJwtToken();
        const res = await fetch(
          "https://whiteintel.io/api/regular/app/v2/user/get_subscription.php",
          {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${token}`,
            },
          }
        );

         if ([401, 403, 419, 440].includes(res.status)) {
              redirectToLogin();
              // throw to stop callers from continuing
              throw new Error(`AUTH_REDIRECT_${res.status}`);
            }

        if (!res.ok) throw new Error(`Error ${res.status}`);
        const data = await res.json();

        if (data?.success) {
          setExportLimit(data.csv_export_limit ?? null);
        }
      } catch (err) {
        console.error("Failed to fetch CSV export limit:", err);
      }
    };

    fetchLimit();
  }, [open]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [infoOpen, setInfoOpen] = useState(true);

  useEffect(() => {
    if (open) {
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const canExport = useMemo(() => {
    const hasType = types.consumer || types.corporate;
    const hasCredits = credits > 0;
    const datesOk =
      (!startDate && !endDate) ||
      (startDate && endDate && new Date(startDate) <= new Date(endDate));
    const hasDomain = domain.trim().length > 0;
    const subdomainAnswered = isSubdomain !== null;
    return hasType && hasCredits && datesOk && hasDomain && subdomainAnswered && !submitting;
  }, [types, credits, startDate, endDate, domain, isSubdomain, submitting]);

  const toggleField = (key) =>
    setFields((f) => ({ ...f, [key]: !f[key] }));
  const toggleType = (key) =>
    setTypes((t) => ({ ...t, [key]: !t[key] }));

  async function handleExport() {
    setError("");
      if (!startDate || !endDate) {
    return setError("You must select date intervals.");   }
    
    if (!domain.trim()) return setError("Please provide a domain.");
    if (isSubdomain === null) return setError("Please specify whether the query is a subdomain.");
    if (!(types.consumer || types.corporate)) return setError("Select at least one data type.");
    if (credits <= 0) return setError("No export credits remaining.");
    if (startDate && endDate && new Date(startDate) > new Date(endDate))
      return setError("Start Date must be before End Date.");

    // Build payload in YOUR API shape
    const payload = {
      query: domain.trim(),
      start_date: startDate || null,
      end_date: endDate || null,
      selectedFields: {
        hostname: !!fields.hostname,
        ipAddress: !!fields.ipAddress,
        osInfo: !!fields.osInfo,
        antivirus: !!fields.antivirus,
        malwarePath: !!fields.malwarePath,
        countryCode: !!fields.countryCode,
      },
      selectedDataTypes: {
        consumer: !!types.consumer,
        corporate: !!types.corporate,
      },
      is_subdomain: isSubdomain,
    };

    try {
      setSubmitting(true);
      await onExport?.(payload);
      // Optionally close on success:
      // onClose?.();
    } catch (e) {
      setError(e?.message || "Export failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {/* Overlay */}
      {open && (
        <div
          onClick={onClose}
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Export CSV"
        className={`fixed right-0 top-0 z-50 h-full w-full max-w-2xl
                    border-l border-sky-900/40
                    bg-[#000414]  backdrop-blur-xl
                    shadow-[0_0_15px_rgba(56,189,248,0.05)]
                    transform transition-transform duration-300
                    ${open ? "translate-x-0" : "translate-x-full"}`}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose?.();
        }}
      >
        {/* Header */}
        <div className="relative flex items-center justify-between border-b border-sky-900/30 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl
                            border border-sky-900/40 bg-sky-500/10">
              {/* export icon */}
              <svg viewBox="0 0 24 24" className="h-5 w-5 text-sky-300" fill="none" stroke="currentColor">
                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      d="M9 12h6m-7 4h8M4 7h16M7 7V4h10v3M7 20h10a3 3 0 003-3V7H4v10a3 3 0 003 3z"/>
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white">Data Export Navigator</h3>
              <div className="text-xs text-neutral-400">Export breach data for offline analysis</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md px-2 py-1 text-neutral-400 hover:text-neutral-200"
            aria-label="Close"
            type="button"
          >
            ✕
          </button>
        </div>



{/* Error bar (sticks under the header, no overlap) */}
{error && (
  <div className="sticky top-0 z-40 px-6 pt-3 bg-transparent">
    <div className="rounded-lg border border-red-500/30 bg-red-500/10 backdrop-blur-sm px-3 py-2 text-sm text-red-200 flex items-start justify-between">
      <span className="pr-4">{error}</span>
      <button
        onClick={() => setError(null)}
        className="ml-3 text-xs text-neutral-400 hover:text-white"
        aria-label="Dismiss error"
        type="button"
      >
        Dismiss
      </button>
    </div>
  </div>
)}


        {/* Body */}
        <div className="h-[calc(100%-64px-76px)] overflow-y-auto p-6 space-y-6">

          {/* Info (collapsible) */}
          <div className="rounded-2xl border border-sky-900/30 bg-gradient-to-b from-white/5 to-white/[0.02] p-4">
            <button
              type="button"
              onClick={() => setInfoOpen((v) => !v)}
              className="w-full flex items-center justify-between text-left"
            >
              <div className="flex items-center gap-3">
                <div className="h-6 w-6 rounded-lg border border-sky-900/40 bg-sky-500/10 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="h-4 w-4 text-sky-300" fill="none" stroke="currentColor">
                    <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"/>
                  </svg>
                </div>
                <div className="text-sky-300 font-semibold">What is this?</div>
              </div>
              <span className="text-neutral-400 text-sm">{infoOpen ? "Hide" : "Show"}</span>
            </button>

            {infoOpen && (
              <div className="mt-3 text-sm text-neutral-300">
                Export breach data to CSV. Include optional device/context fields and choose record types.
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                  <div><span className="text-neutral-400">URL</span> — Affected Application</div>
                  <div><span className="text-neutral-400">Username</span> — Compromised account</div>
                  <div><span className="text-neutral-400">Password</span> — Leaked password</div>
                  <div><span className="text-neutral-400">Device Info</span> — IP, OS, AV, etc.</div>
                </div>
                <div className="mt-4 text-xs text-neutral-400 leading-relaxed">
                  <div className="font-semibold text-neutral-300 mb-1">Export limits by license:</div>
                  <div>Researcher: 10 exports/month, max 2-months interval per export.</div>
                  <div>Enterprise: 10 exports/day, max 1-year interval per export.</div>
                  <div>Threat Intelligence: 500 exports/day, max 2-years interval per export.</div>
                </div>
              </div>
            )}
          </div>

          {/* Credits card */}
<div className="rounded-2xl border border-sky-900/30 bg-white/[0.04] p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
  <div>
    <div className="text-sm text-neutral-300">Your remaining CSV Export credits</div>
    <div className={`mt-1 text-2xl font-semibold ${exportLimit > 0 ? "text-white" : "text-red-300"}`}>
      {exportLimit !== null ? exportLimit : "—"}
    </div>
    {exportLimit <= 0 && (
      <div className="mt-1 text-xs">
        <Link 
          to="/upgrades" 
          className="text-sky-400 hover:text-sky-300 underline"
        >
          Upgrade your plan to access more export credits.
        </Link>
      </div>
    )}
  </div>
  
  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs border
                    ${exportLimit > 0
                      ? "border-emerald-500/30 text-emerald-200 bg-emerald-500/10"
                      : "border-red-500/30 text-red-200 bg-red-500/10"}`}>
    {exportLimit > 0 ? "Available" : "Insufficient credits"}
  </span>
</div>


          {/* Query + Date card */}
          <div className="rounded-2xl border border-sky-900/30 bg-white/[0.04] p-4 space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-lg border border-sky-900/40 bg-sky-500/10 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="h-4 w-4 text-sky-300" fill="none" stroke="currentColor">
                  <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"/>
                </svg>
              </div>
              <div className="text-sm font-semibold text-white">Query</div>
            </div>
            <input
              type="text"
              placeholder={query}
              defaultValue={query}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/60 px-3 py-2 text-base text-white outline-none focus:ring-2 focus:ring-sky-500/30"
            />

            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-neutral-300">
                Is the query a subdomain? <span className="text-red-400 text-xs font-semibold">*</span>
              </span>
              {[
                { value: true,  label: "Yes" },
                { value: false, label: "No" },
              ].map((opt) => (
                <label
                  key={String(opt.value)}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer
                              ${isSubdomain === opt.value
                                ? "border-sky-500/40 bg-sky-500/10 text-white"
                                : "border-sky-900/30 bg-neutral-900/40 text-neutral-300 hover:bg-neutral-900"}`}
                >
                  <input
                    type="radio"
                    name="isSubdomain"
                    checked={isSubdomain === opt.value}
                    onChange={() => setIsSubdomain(opt.value)}
                    className="accent-sky-500"
                  />
                  {opt.label}
                </label>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <div className="h-6 w-6 rounded-lg border border-sky-900/40 bg-sky-500/10 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 text-sky-300" fill="none" stroke="currentColor">
                      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            d="M8 7V3m8 4V3M3 11h18M5 19h14a2 2 0 002-2v-6H3v6a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <div className="text-sm font-semibold text-white">Start Date <span className="text-red-400 text-xs font-semibold">*</span></div>
                </div>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/60 px-3 py-2 text-base text-white outline-none focus:ring-2 focus:ring-sky-500/30"
                />
              </div>
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <div className="h-6 w-6 rounded-lg border border-sky-900/40 bg-sky-500/10 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 text-sky-300" fill="none" stroke="currentColor">
                      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            d="M8 7V3m8 4V3M3 11h18M5 19h14a2 2 0 002-2v-6H3v6a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <div className="text-sm font-semibold text-white">End Date <span className="text-red-400 text-xs font-semibold">*</span></div>
                </div>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/60 px-3 py-2 text-base text-white outline-none focus:ring-2 focus:ring-sky-500/30"
                />
              </div>
            </div>
          </div>

          {/* Selections card */}
          <div className="rounded-2xl border border-sky-900/30 bg-white/[0.04] p-4 space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Additional Data Fields */}
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <div className="h-6 w-6 rounded-lg border border-sky-900/40 bg-sky-500/10 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 text-sky-300" fill="none" stroke="currentColor">
                      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            d="M4 7h16M4 12h16M4 17h16"/>
                    </svg>
                  </div>
                  <div className="text-sm font-semibold text-white">Additional Data Fields</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: "hostname",    label: "hostname" },
                    { key: "ipAddress",   label: "ip Address" },
                    { key: "osInfo",      label: "os Info" },
                    { key: "antivirus",   label: "antivirus" },
                    { key: "malwarePath", label: "malware Path" },
                    { key: "countryCode", label: "country Code" },
                  ].map((f) => (
                    <label
                      key={f.key}
                      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm
                                  ${fields[f.key]
                                    ? "border-sky-500/40 bg-sky-500/10 text-white"
                                    : "border-sky-900/30 bg-neutral-900/40 text-neutral-300 hover:bg-neutral-900"}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!fields[f.key]}
                        onChange={() => toggleField(f.key)}
                        className="accent-sky-500"
                      />
                      {f.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Data Types */}
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <div className="h-6 w-6 rounded-lg border border-sky-900/40 bg-sky-500/10 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 text-sky-300" fill="none" stroke="currentColor">
                      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            d="M8 12h3m-6 8h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <div className="text-sm font-semibold text-white">Data Types</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "consumer",  label: "Consumer Records" },
                    { key: "corporate", label: "Corporate Records" },
                  ].map((o) => (
                    <label
                      key={o.key}
                      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm
                                  ${types[o.key]
                                    ? "border-sky-500/40 bg-sky-500/10 text-white"
                                    : "border-sky-900/30 bg-neutral-900/40 text-neutral-300 hover:bg-neutral-900"}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!types[o.key]}
                        onChange={() => toggleType(o.key)}
                        className="accent-sky-500"
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

     
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t border-sky-900/30 bg-[#0a1224]/80 backdrop-blur-xl px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                setDomain(defaultDomain);
                setStartDate(defaultStart);
                setEndDate(defaultEnd);
                setFields({
                  hostname: false, ipAddress: false, osInfo: false,
                  antivirus: false, malwarePath: false, countryCode: false,
                });
                setTypes({ consumer: false, corporate: false });
                setIsSubdomain(null);
                setError("");
              }}
              className="text-base text-neutral-400 hover:text-neutral-200"
            >
              Reset
            </button>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-sky-900/40 bg-neutral-900/40 px-4 py-2 text-base text-neutral-200 hover:bg-neutral-900"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExport}
                disabled={!canExport}
                className={`rounded-lg border px-4 py-2 text-base
                            ${canExport
                              ? "border-sky-600/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                              : "border-neutral-700 bg-neutral-900/50 text-neutral-400 cursor-not-allowed"}`}
              >
                {submitting ? "Exporting…" : "Export CSV"}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
